import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auditsAPI, coreAPI, type Audit, type AuditStartInput, type AuditTemplate } from '../../lib/api';
import { Button } from '../../components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import SearchableSelect from '../../components/ui/SearchableSelect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { AlertCircle, Loader2 } from 'lucide-react';
import { supplierPickerLabel } from '../../lib/questionnaireLabels';
import { defaultAuditName } from './auditStatus';
import { auditTitle } from './auditStatus';
import { apiError } from './useAuditActions';

interface StartAuditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** From a supplier profile: the supplier is fixed. */
  supplier?: { id: number; name: string } | null;
}

const LAST_TEMPLATE_KEY = 'helix_last_audit_template';
const NAME_MAX = 200;

function rememberedTemplate(): string {
  try { return window.localStorage.getItem(LAST_TEMPLATE_KEY) ?? ''; } catch { return ''; }
}

/**
 * Start an audit: supplier + audit template and an optional name. Opens the
 * audit straight away; when one of that supplier with that template is already
 * in progress the auditor chooses: Continue it, or Start new audit.
 */
export default function StartAuditDialog({ open, onOpenChange, supplier = null }: StartAuditDialogProps) {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<AuditTemplate[]>([]);
  const [suppliers, setSuppliers] = useState<Array<{ value: string; label: string }>>([]);
  const [supplierId, setSupplierId] = useState<string>('');
  const [templateId, setTemplateId] = useState<string>('');
  const [name, setName] = useState<string>('');
  /** The auditor typed a name; otherwise the field follows the default and the server names it. */
  const [nameTouched, setNameTouched] = useState<boolean>(false);
  const [supplierNames, setSupplierNames] = useState<Record<string, string>>({});
  const [inProgress, setInProgress] = useState<Audit | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadFailed, setLoadFailed] = useState<boolean>(false);
  const [starting, setStarting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setLoadFailed(false);
    Promise.all([
      auditsAPI.templates(),
      // Suppliers still being onboarded too: a factory audit often comes before approval.
      supplier ? Promise.resolve(null) : coreAPI.getMyConnections({ limit: 2000, include_onboarding: true }),
    ]).then(([tpls, connections]) => {
      const audits = tpls.filter((t) => t.is_audit);
      setTemplates(audits);
      const remembered = rememberedTemplate();
      const pick = audits.length === 1 ? String(audits[0]?.id)
        : (audits.some((t) => String(t.id) === remembered) ? remembered : '');
      setTemplateId(pick);
      if (connections) {
        const rows = connections.data.filter((c) => c.as_supplier);
        setSuppliers(rows.map((c) => ({ value: String(c.id), label: supplierPickerLabel(c) })));
        setSupplierNames(Object.fromEntries(rows.map((c) => [String(c.id), c.name])));
      }
    }).catch((err: unknown) => {
      console.error('Failed to load audit setup:', err);
      setLoadFailed(true);
    }).finally(() => setLoading(false));
  }, [supplier]);

  useEffect(() => {
    if (!open) return;
    setSupplierId(supplier ? String(supplier.id) : '');
    setName('');
    setNameTouched(false);
    setError(null);
    load();
  }, [open, supplier, load]);

  // An audit of this pair already in progress: continue it, or start a new one on purpose.
  useEffect(() => {
    setInProgress(null);
    if (!open || !supplierId || !templateId) return;
    let cancelled = false;
    auditsAPI.list({ supplier: Number(supplierId), status: 'in_progress' })
      .then((rows) => { if (!cancelled) setInProgress(rows.find((a) => String(a.template) === templateId) ?? null); })
      .catch(() => { /* the server still continues an existing audit on start */ });
    return () => { cancelled = true; };
  }, [open, supplierId, templateId]);

  const openAudit = (auditId: number) => {
    try { window.localStorage.setItem(LAST_TEMPLATE_KEY, templateId); } catch { /* storage unavailable */ }
    onOpenChange(false);
    navigate(`/audits/${auditId}`);
  };

  /** `isNew`: a second audit of this supplier + template alongside the one in progress. */
  // The name follows the chosen supplier and template until the auditor types one.
  const supplierName = supplier?.name ?? supplierNames[supplierId] ?? '';
  const templateName = templates.find((t) => String(t.id) === templateId)?.name ?? '';
  useEffect(() => {
    if (nameTouched) return;
    setName(supplierName && templateName ? defaultAuditName(supplierName, templateName) : '');
  }, [supplierName, templateName, nameTouched]);

  const start = async (isNew: boolean) => {
    if (!supplierId || !templateId) return;
    setStarting(true);
    setError(null);
    const payload: AuditStartInput = { supplier: Number(supplierId), template: Number(templateId) };
    const trimmed = name.trim();
    if (nameTouched && trimmed) payload.name = trimmed;
    if (isNew) payload.new = true;
    try {
      const audit = await auditsAPI.start(payload);
      openAudit(audit.id);
    } catch (err: unknown) {
      setError(apiError(err, 'Could not start the audit.'));
    } finally {
      setStarting(false);
    }
  };

  const ready = Boolean(supplierId && templateId) && !starting && !loading && !loadFailed;
  const existingTitle = inProgress ? auditTitle(inProgress) : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Start an audit</DialogTitle>
          <DialogDescription>
            Choose the supplier and audit template. You complete it yourself — the supplier won&apos;t see it.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
          </div>
        ) : loadFailed ? (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <AlertCircle className="h-6 w-6 text-destructive" />
            <p className="text-sm">Could not load suppliers and audit templates.</p>
            <Button variant="outline" onClick={load}>Retry</Button>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="start-audit-supplier">Supplier</Label>
              {supplier ? (
                <p id="start-audit-supplier" className="flex h-12 items-center rounded-md border bg-muted px-4 text-base">{supplier.name}</p>
              ) : (
                <SearchableSelect id="start-audit-supplier" options={suppliers} value={supplierId} size="lg"
                  onChange={setSupplierId} placeholder="Choose a supplier" />
              )}
            </div>
            <div className="space-y-2">
              <Label>Audit template</Label>
              {templates.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No audit templates yet. An admin can mark templates as audits under Audit templates.
                </p>
              ) : (
                <Select value={templateId} onValueChange={setTemplateId}>
                  <SelectTrigger className="h-12 text-base" aria-label="Audit template">
                    <SelectValue placeholder="Choose a template" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={String(t.id)} className="py-3 text-base">{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="start-audit-name">
                Audit name
              </Label>
              <Input id="start-audit-name" className="h-12 text-base" value={name} maxLength={NAME_MAX}
                onChange={(e) => { setName(e.target.value); setNameTouched(true); }}
                placeholder="Supplier – template – date"
                aria-describedby="start-audit-name-help" />
              <p id="start-audit-name-help" className="text-sm text-muted-foreground">
                Defaults to supplier – template – date. Change it if you like, e.g. the product signed off
              </p>
            </div>
            {inProgress && (
              <p className="rounded-md bg-muted px-3 py-2 text-sm" data-testid="audit-in-progress-note">
                <span className="font-medium">{existingTitle}</span> is already in progress for this supplier with this
                template ({inProgress.completion_percentage}% answered). Continue it, or start a new audit.
              </p>
            )}
          </div>
        )}
        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        <DialogFooter>
          <Button size="lg" variant="outline" onClick={() => onOpenChange(false)} disabled={starting}>Cancel</Button>
          {inProgress ? (
            <>
              <Button size="lg" variant="outline" onClick={() => void start(true)} disabled={!ready}>
                {starting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Start new audit
              </Button>
              <Button size="lg" className="max-w-full sm:max-w-[18rem]" onClick={() => openAudit(inProgress.id)} disabled={!ready}
                title={`Continue ${existingTitle}`}>
                <span className="truncate">Continue {existingTitle}</span>
              </Button>
            </>
          ) : (
            <Button size="lg" onClick={() => void start(false)} disabled={!ready}>
              {starting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Start audit
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
